import type { MedplumClient } from '@medplum/core';
import type { Appointment, AppointmentParticipant, QuestionnaireResponse } from '@medplum/fhirtypes';
import { ATTENDANCE_SOURCE_EXT } from '@unify/fhir-model/constants';
import {
  isResolvedClientAttendance,
  participantRsvpForAttendance,
  setParticipantAttendanceStatus,
} from '@unify/fhir-model/resources/appointment';
import { getDetailedStatus } from '@unify/fhir-model/resources/questionnaire-response';
import {
  DEFAULT_PARTICIPANT_ATTENDANCE_STATUS,
  type ParticipantAttendanceStatus,
  getDefaultParticipantAttendanceFromAppointment,
  mergeParticipantAttendanceIntoResponseItems,
  resolveParticipantAttendanceFromAppointmentParticipant,
} from '@/pages/appointments/utils/participantAttendance';
import { appointmentNotePages } from '@/features/calendar/signedNoteAppointmentLock';
import { normalizeCalendarClientAttendanceStatus } from '@/lib/calendarClientAttendanceStatus';
import {
  cascadeAppointmentEncountersAfterWrite,
  isSignedNoteLockError,
  writeAppointmentAttendance,
} from '@/pages/appointments/utils/attendanceFulfillment';

const ATTENDANCE_SOURCE_SYSTEM = 'https://unify.health/fhir/CodeSystem/attendance-source';

function coerceSessionAttendanceStatus(
  status: ParticipantAttendanceStatus | string | null | undefined,
): ParticipantAttendanceStatus | null {
  if (status == null) return null;
  return normalizeCalendarClientAttendanceStatus(status) ?? DEFAULT_PARTICIPANT_ATTENDANCE_STATUS;
}

/** Read calendar attendance from participant extension (falls back to appointment context). */
export function readParticipantAppointmentAttendance(
  participant: AppointmentParticipant,
  appointment: Appointment,
): ParticipantAttendanceStatus {
  return resolveParticipantAttendanceFromAppointmentParticipant(participant, appointment);
}

export function applyParticipantAttendanceToParticipant(
  participant: AppointmentParticipant,
  status: ParticipantAttendanceStatus | null,
): AppointmentParticipant {
  const normalized = status != null ? coerceSessionAttendanceStatus(status) : null;
  const next: AppointmentParticipant = { ...participant };
  setParticipantAttendanceStatus(next, normalized ?? undefined);
  const rsvp = participantRsvpForAttendance(normalized ?? undefined);
  if (rsvp) next.status = rsvp;
  return next;
}

/** The `manual` attendance-source Coding every UI path that stamps the source uses. */
export const MANUAL_ATTENDANCE_SOURCE_CODING = {
  system: ATTENDANCE_SOURCE_SYSTEM,
  code: 'manual',
  display: 'Manual',
};

export function setAppointmentAttendanceSourceManual(appointment: Appointment): Appointment {
  const others = (appointment.extension ?? []).filter((e) => e.url !== ATTENDANCE_SOURCE_EXT);
  return {
    ...appointment,
    extension: [
      ...others,
      {
        url: ATTENDANCE_SOURCE_EXT,
        valueCoding: MANUAL_ATTENDANCE_SOURCE_CODING,
      },
    ],
  };
}

export interface SessionAttendanceEntry {
  patientId: string;
  status: ParticipantAttendanceStatus | null;
}

export function readInitialAttendanceFromAppointment(
  appointment: Appointment,
): Map<string, ParticipantAttendanceStatus> {
  const fromAppointment = getDefaultParticipantAttendanceFromAppointment(appointment);
  return new Map(Object.entries(fromAppointment));
}

export function applyAttendanceEntriesToAppointment(
  appointment: Appointment,
  entries: SessionAttendanceEntry[],
): Appointment {
  const byPatient = new Map(entries.map((e) => [e.patientId, e.status]));
  let next: Appointment = {
    ...appointment,
    participant: (appointment.participant ?? []).map((p) => {
      const ref = p.actor?.reference ?? '';
      const patientId = ref.startsWith('Patient/') ? ref.replace('Patient/', '') : '';
      if (!patientId || !byPatient.has(patientId)) return p;
      return applyParticipantAttendanceToParticipant(p, byPatient.get(patientId) ?? null);
    }),
  };
  // Same resolved check as the write path: a `scheduled` pick never shows `manual`.
  if (entries.some((e) => isResolvedClientAttendance(coerceSessionAttendanceStatus(e.status) ?? undefined))) {
    next = setAppointmentAttendanceSourceManual(next);
  }
  return next;
}

function attendanceRowsFromAppointment(appointment: Appointment): { patientId: string; displayName: string }[] {
  return (appointment.participant ?? [])
    .filter((p) => p.actor?.reference?.startsWith('Patient/'))
    .map((p) => {
      const patientId = p.actor!.reference!.replace('Patient/', '');
      return { patientId, displayName: p.actor?.display?.trim() || 'Client' };
    });
}

const LOCKED_NOTE_STATUSES = new Set(['signed', 'pending-cosign', 'cosigned']);

function isLockedClinicalNote(qr: QuestionnaireResponse): boolean {
  const detailed = getDetailedStatus(qr);
  return detailed != null && LOCKED_NOTE_STATUSES.has(detailed);
}

/**
 * Persist attendance like the appointment progress note: Appointment.participant extension
 * plus participant-attendance on any appointment-tagged QuestionnaireResponse.
 */
export async function persistSessionAttendance(
  medplum: MedplumClient,
  appointment: Appointment,
  entries: SessionAttendanceEntry[],
): Promise<Appointment> {
  if (!appointment.id) return appointment;

  const attendance = entries.flatMap((entry) => {
    const status = coerceSessionAttendanceStatus(entry.status);
    if (!status) return [];
    return [{ participantReference: `Patient/${entry.patientId}`, attendanceStatus: status }];
  });

  let savedAppt: Appointment;
  try {
    savedAppt = await writeAppointmentAttendance(medplum, {
      appointmentId: appointment.id,
      appointment,
      attendance,
      markManualSource: attendance.some((entry) => isResolvedClientAttendance(entry.attendanceStatus)),
    });
  } catch (err) {
    if (isSignedNoteLockError(err)) return appointment;
    throw err;
  }
  if (savedAppt === appointment) return appointment;
  await cascadeAppointmentEncountersAfterWrite(medplum, savedAppt);
  const normalizedAppt = applyAttendanceEntriesToAppointment(savedAppt, entries);

  const apptId = appointment.id;
  if (!apptId) return normalizedAppt;

  const byPatient: Record<string, ParticipantAttendanceStatus> = {};
  for (const e of entries) {
    const coerced = coerceSessionAttendanceStatus(e.status);
    if (coerced) byPatient[e.patientId] = coerced;
  }
  const rowMeta = attendanceRowsFromAppointment(normalizedAppt);
  if (rowMeta.length === 0) return normalizedAppt;

  const qrs: QuestionnaireResponse[] = [];
  try {
    for await (const page of appointmentNotePages(medplum, apptId)) qrs.push(...page);
  } catch {
    // Note sync is best-effort: update whichever notes were fetched.
  }

  await Promise.all(
    qrs.map(async (qr) => {
      if (!qr.id || isLockedClinicalNote(qr)) return;
      try {
        const item = mergeParticipantAttendanceIntoResponseItems(qr.item, byPatient, rowMeta);
        await medplum.updateResource({ ...qr, item });
      } catch {
        // Appointment.participant is the source of truth; note sync must not roll back that write.
      }
    }),
  );

  return normalizedAppt;
}
